import {
  BusinessContext,
  DateRangeProvider,
  FeatureContextProvider,
  PermissionContextProviderV2,
  RealtimeProvider
} from '@context'
import {
  collectFromPages,
  fetchBusiness,
  IBusiness,
  PaginatedResponse,
  useCurrentUser
} from '@query'
import React from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { AxiosError } from 'axios'
import { Redirect, useParams } from 'react-router-dom'
import { SplashSignal } from '@components/SplashSignal'
import { useMarketHostRedirect } from '@hooks'
import { BusinessPage } from './BusinessPage'

interface BusinessBootstrapLayoutProps {
  baseUrl: string
}

export const BusinessBootstrapLayout: React.FC<BusinessBootstrapLayoutProps> = ({ baseUrl }) => {
  const { slug } = useParams<{ slug: string }>()
  const queryClient = useQueryClient()

  useCurrentUser()

  const {
    data: business,
    isLoading,
    error: businessError
  } = useQuery<IBusiness, AxiosError>({
    queryKey: [slug],
    queryFn: () => fetchBusiness({ slug }),
    enabled: Boolean(slug),
    retry: false,
    initialData: () => {
      const businessesQueryData = queryClient
        .getQueriesData<PaginatedResponse<IBusiness>>({ queryKey: ['businesses'] })
        .map(([, data]) => data)
        .filter(Boolean)

      const businesses = collectFromPages<IBusiness>(businessesQueryData as any)
      return businesses.find(b => b.slug === slug)
    }
  })

  useMarketHostRedirect(business)

  if (businessError?.response?.status === 403) {
    return <Redirect to="/start" />
  }

  return (
    <>
      {isLoading ? (
        <SplashSignal source={`business-bootstrap-loading-${slug}`} />
      ) : (
        <BusinessContext.Provider value={{ slug, data: business }}>
          <PermissionContextProviderV2>
            <DateRangeProvider>
              <FeatureContextProvider>
                <RealtimeProvider slug={slug}>
                  <BusinessPage baseUrl={baseUrl} />
                </RealtimeProvider>
              </FeatureContextProvider>
            </DateRangeProvider>
          </PermissionContextProviderV2>
        </BusinessContext.Provider>
      )}
    </>
  )
}

export default BusinessBootstrapLayout
